import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Editor } from 'ketcher-react';
import { StandaloneStructServiceProvider } from 'ketcher-standalone';
import 'ketcher-react/dist/index.css';
import './StructureEditor.css';
import API_BASE from '../config';

const structServiceProvider = new StandaloneStructServiceProvider();

const API_BASE_URL = API_BASE;

const StructureEditor = ({ molblock, smiles, onSave, onClose }) => {
  const editorRef = useRef(null);
  const [smilesInput, setSmilesInput] = useState('');
  const [loadError, setLoadError] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const handleInit = useCallback((editor) => {
    editorRef.current = editor;
    // molblock takes priority: it encodes stereo geometrically via wedge/hash
    // bonds, which Ketcher's chemistry engine (Indigo) handles far more
    // reliably than re-parsing stereo-SMILES notation (which can silently
    // mangle certain valid SMILES, e.g. explicit [H] atoms combined with
    // ring-closure stereocenters, into corrupted query atoms).
    const structure = molblock || smiles || '';
    if (structure) {
      setTimeout(() => {
        editor.setMolecule(structure)
          .then(() => editor.layout().catch(() => {}))
          .catch(console.error);
      }, 1000);
    }
  }, [molblock, smiles]);

  const handleLoadSmiles = useCallback(async () => {
    const s = smilesInput.split(' |')[0].trim();
    if (!s || !editorRef.current) return;
    try {
      await editorRef.current.setMolecule(s);
      await editorRef.current.layout().catch(() => {});
      setSmilesInput('');
      setLoadError('');
    } catch {
      setLoadError('Could not parse SMILES');
    }
  }, [smilesInput]);

  const handleLoadName = useCallback(async () => {
    const name = nameInput.trim();
    if (!name || !editorRef.current) return;
    setNameLoading(true);
    setNameError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/name-to-smiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Not found');
      await editorRef.current.setMolecule(data.smiles);
      await editorRef.current.layout().catch(() => {});
      setNameInput('');
    } catch (err) {
      setNameError(err.message);
    } finally {
      setNameLoading(false);
    }
  }, [nameInput]);

  const handleSave = async () => {
    if (!editorRef.current) return;
    try {
      const rawSmiles = await editorRef.current.getSmiles();
      const newSmiles = rawSmiles.split(' |')[0].trim();
      const newMolblock = await editorRef.current.getMolfile();
      onSave(newSmiles, newMolblock);
    } catch (err) {
      console.error('Failed to get structure from editor:', err);
    }
  };

  return (
    <div className="editor-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="editor-modal">
        <div className="editor-header">
          <h3>Edit Structure</h3>
          <div className="editor-actions">
            <button className="editor-save-btn" onClick={handleSave}>Save</button>
            <button className="editor-cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </div>
        <div className="smiles-paste-bar">
          <input
            className="smiles-paste-input"
            type="text"
            placeholder="Paste SMILES and press Enter to load…"
            value={smilesInput}
            onChange={e => { setSmilesInput(e.target.value); setLoadError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLoadSmiles()}
          />
          <button className="smiles-paste-btn" onClick={handleLoadSmiles}>Load</button>
          {loadError && <span className="smiles-paste-error">{loadError}</span>}
        </div>
        <div className="smiles-paste-bar name-lookup-bar">
          <input
            className="smiles-paste-input"
            type="text"
            placeholder="Enter name or IUPAC and press Enter to load…"
            value={nameInput}
            onChange={e => { setNameInput(e.target.value); setNameError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLoadName()}
            disabled={nameLoading}
          />
          <button className="smiles-paste-btn name-lookup-btn" onClick={handleLoadName} disabled={nameLoading}>
            {nameLoading ? '…' : 'Lookup'}
          </button>
          {nameError && <span className="smiles-paste-error">{nameError}</span>}
        </div>
        <div className="editor-body">
          <Editor
            staticResourcesUrl=""
            structServiceProvider={structServiceProvider}
            onInit={handleInit}
            errorHandler={console.error}
          />
        </div>
      </div>
    </div>
  );
};

export default StructureEditor;
